import type { ReactNode } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import {
  AllInclusive,
  Autorenew,
  BadgeOutlined,
  CalendarTodayOutlined,
  ConfirmationNumberOutlined,
  PhoneDisabledOutlined,
  PhoneInTalkOutlined,
  QrCode2Outlined,
  RouterOutlined,
  SimCardOutlined,
  SmartphoneOutlined,
  SpeedOutlined,
  StorageOutlined,
} from '@mui/icons-material';

import { ProductSaleType, ProductType } from '@apis/product';
import type { ProductSpecification } from '@models/product';
import { RequiredDataType } from '@models/product';
import { getProductTypeMeta } from '@utils/productLabels';

const specIconSx = {
  fontSize: 16,
  flex: '0 0 auto',
} as const;

export const toSafeNumber = (value: unknown, fallback = 0) => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const formatDays = (value: unknown) => {
  const days = toSafeNumber(value, 0);
  if (days <= 0) return '-';
  return days === 1 ? '1 day' : `${days.toLocaleString()} days`;
};

export const isRefreshableData = (spec?: Partial<ProductSpecification> | null) => {
  if (spec?.isUnlimitedData) return false;
  const unit = String(spec?.dataCapacity?.refreshPeriodUnit ?? '');
  const value = toSafeNumber(spec?.dataCapacity?.refreshPeriodValue, 0);
  return unit !== 'NEVER' && value > 0;
};

export const formatDataCapacity = (spec?: Partial<ProductSpecification> | null) => {
  if (spec?.isUnlimitedData) return 'Unlimited';

  const quantity = toSafeNumber(spec?.dataCapacity?.quantity, 0);
  const unit = String(spec?.dataCapacity?.unit ?? '').trim();
  if (quantity <= 0 || !unit) return '-';

  return `${quantity.toLocaleString()} ${unit}`;
};

export const formatRefreshCycle = (spec?: Partial<ProductSpecification> | null) => {
  if (spec?.isUnlimitedData) return 'No refresh';
  if (!isRefreshableData(spec)) return 'No refresh';

  const value = toSafeNumber(spec?.dataCapacity?.refreshPeriodValue, 0);
  const unit = String(spec?.dataCapacity?.refreshPeriodUnit ?? '').toLowerCase();
  if (value <= 0 || !unit) return 'No refresh';

  return `Every ${value.toLocaleString()} ${unit}`;
};

type InlineSpecDisplayProps = {
  icon?: ReactNode;
  label: string;
  color: string;
  muted?: boolean;
};

export const InlineSpecDisplay = ({
  icon,
  label,
  color,
  muted = false,
}: InlineSpecDisplayProps) => (
  <Stack
    component="span"
    direction="row"
    spacing={0.6}
    alignItems="center"
    sx={{
      color,
      minWidth: 0,
      opacity: muted ? 0.72 : 1,
      whiteSpace: 'nowrap',
    }}
  >
    {icon}
    <Typography
      component="span"
      variant="body2"
      sx={{
        color: 'inherit',
        fontWeight: muted ? 500 : 800,
        lineHeight: 1.25,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  </Stack>
);

export const getDataTypeMeta = (spec?: Partial<ProductSpecification> | null) => {
  if (spec?.isUnlimitedData) {
    return {
      label: 'Unlimited',
      color: 'success.main',
      icon: <AllInclusive sx={specIconSx} />,
    };
  }

  if (isRefreshableData(spec)) {
    return {
      label: 'Daily Reset',
      color: 'info.main',
      icon: <Autorenew sx={specIconSx} />,
    };
  }

  return {
    label: 'Fixed Data',
    color: 'text.primary',
    icon: <StorageOutlined sx={specIconSx} />,
  };
};

export const getProductTypeDisplayMeta = (productType?: ProductType | string | null) => {
  const meta = getProductTypeMeta(productType);

  switch (productType) {
    case ProductType.PSIM_TOPUP:
      return {
        label: 'Top-up',
        color: 'warning.main',
        icon: <SimCardOutlined sx={specIconSx} />,
      };
    case ProductType.RESERVE_ESIM:
      return {
        label: meta.label,
        color: 'secondary.main',
        icon: <ConfirmationNumberOutlined sx={specIconSx} />,
      };
    case ProductType.ROUTER:
      return {
        label: meta.label,
        color: 'text.primary',
        icon: <RouterOutlined sx={specIconSx} />,
      };
    case ProductType.ESIM:
      return {
        label: meta.label,
        color: 'info.main',
        icon: <QrCode2Outlined sx={specIconSx} />,
      };
    default:
      return {
        label: meta.label,
        color: 'text.secondary',
        icon: null,
      };
  }
};

export const ProductTypeDisplay = ({
  productType,
}: {
  productType?: ProductType | string | null;
}) => {
  const meta = getProductTypeDisplayMeta(productType);

  return <InlineSpecDisplay icon={meta.icon} label={meta.label} color={meta.color} />;
};

export const ProductDataTypeDisplay = ({
  specification,
}: {
  specification?: Partial<ProductSpecification> | null;
}) => {
  const meta = getDataTypeMeta(specification);

  return <InlineSpecDisplay icon={meta.icon} label={meta.label} color={meta.color} />;
};

export const ProductDataCapacityDisplay = ({
  specification,
}: {
  specification?: Partial<ProductSpecification> | null;
}) => {
  const label = formatDataCapacity(specification);

  return (
    <InlineSpecDisplay
      icon={<StorageOutlined sx={specIconSx} />}
      label={label}
      color={label === '-' ? 'text.disabled' : 'text.primary'}
      muted={label === '-'}
    />
  );
};

export const ProductValidityDisplay = ({
  dayCount,
}: {
  dayCount?: number | string | null;
}) => {
  const label = formatDays(dayCount);

  return (
    <InlineSpecDisplay
      icon={<CalendarTodayOutlined sx={specIconSx} />}
      label={label}
      color={label === '-' ? 'text.disabled' : 'text.primary'}
      muted={label === '-'}
    />
  );
};

export const ProductFupDisplay = ({
  specification,
}: {
  specification?: Partial<ProductSpecification> | null;
}) => {
  const hasFup = Boolean(specification?.isFup);

  return (
    <InlineSpecDisplay
      icon={hasFup ? <SpeedOutlined sx={specIconSx} /> : undefined}
      label={hasFup ? 'FUP' : '-'}
      color={hasFup ? 'warning.main' : 'text.disabled'}
      muted={!hasFup}
    />
  );
};

export const ProductVoiceDisplay = ({
  specification,
}: {
  specification?: Partial<ProductSpecification> | null;
}) => {
  const hasVoice = Boolean(specification?.isVoiceCallSupported);

  return (
    <InlineSpecDisplay
      icon={hasVoice ? <PhoneInTalkOutlined sx={specIconSx} /> : <PhoneDisabledOutlined sx={specIconSx} />}
      label={hasVoice ? 'Voice' : 'Data only'}
      color={hasVoice ? 'success.main' : 'text.secondary'}
      muted={!hasVoice}
    />
  );
};

export type RequiredInfoItem = {
  key: string;
  label: string;
  tooltip: string;
  icon: ReactNode;
  color: string;
  required: boolean;
};

export const getRequiredInfoItems = (
  requiredFields: unknown,
  saleType?: ProductSaleType | string | null,
): RequiredInfoItem[] => {
  const fields = Array.isArray(requiredFields)
    ? requiredFields.map((field) => String(field))
    : [];

  const isReservation = saleType === ProductSaleType.RESERVATION;
  const hasActivationDate = fields.includes(RequiredDataType.ACTIVATION_DATE);
  const activationLabel = isReservation ? 'Pickup Date' : 'Activation Date';

  const hasImei = fields.includes(RequiredDataType.IMEI);
  const hasEid = fields.includes(RequiredDataType.EID);
  const requiredIds = [hasImei ? 'IMEI' : null, hasEid ? 'EID' : null]
    .filter(Boolean)
    .join(' and ');
  const hasPassportData = fields.includes(RequiredDataType.PASSPORT_DATA);

  return [
    {
      key: 'activation',
      label: activationLabel,
      tooltip: hasActivationDate
        ? `${activationLabel}: required`
        : `${activationLabel}: not required`,
      icon: <CalendarTodayOutlined sx={specIconSx} />,
      color: 'info.main',
      required: hasActivationDate,
    },
    {
      key: 'device',
      label: 'Device ID',
      tooltip: requiredIds
        ? `Device ID: ${requiredIds} required`
        : 'Device ID: not required',
      icon: <SmartphoneOutlined sx={specIconSx} />,
      color: 'primary.main',
      required: Boolean(requiredIds),
    },
    {
      key: 'passport',
      label: 'Passport Data',
      tooltip: hasPassportData
        ? 'Passport Data: required for identity verification'
        : 'Passport Data: not required',
      icon: <BadgeOutlined sx={specIconSx} />,
      color: 'secondary.main',
      required: hasPassportData,
    },
  ];
};

export const RequiredInfoIcons = ({
  requiredFields,
  saleType,
  showInactive = true,
  inactiveOpacity = 0.22,
}: {
  requiredFields: unknown;
  saleType?: ProductSaleType | string | null;
  showInactive?: boolean;
  inactiveOpacity?: number;
}) => {
  const items = getRequiredInfoItems(requiredFields, saleType).filter(
    (item) => showInactive || item.required,
  );

  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
      {items.map((item) => (
        <Tooltip key={item.key} title={item.tooltip} arrow>
          <Box
            component="span"
            sx={{
              color: item.required ? item.color : 'text.disabled',
              display: 'inline-flex',
              alignItems: 'center',
              opacity: item.required ? 1 : inactiveOpacity,
            }}
            aria-label={item.label}
          >
            {item.icon}
          </Box>
        </Tooltip>
      ))}
    </Stack>
  );
};
